import { forEach } from 'lodash';
import { Observer } from 'rxjs';
import service from 'src/ServiceContainer';
import { EventSourceContext } from './ServerSentEventSource.types';
import { API_BASE_URL } from 'src/state/ViewConfig/ViewConfig.slice';

export class ServerSentEventSource {
  private context: EventSourceContext = 'assortment'; // default to assortment for now
  private observer: Observer<Event>;
  private eventOpened = false;
  private eventSource: EventSource | undefined;
  private expectedEventTypes: Set<string> = new Set();
  private receivedEventTypes = new Set<string>();
  private noEventsConnectionTimeout: NodeJS.Timeout | undefined = undefined;
  private missingEventTypesTimeout: NodeJS.Timeout | undefined = undefined;
  accessToken: string;
  eventTypes: string[];

  // #region retry vars
  private latestEventTime = new Date();
  private MAX_EVENT_GAP_SECONDS = 30;
  private MAX_RETRY_GAP_MS = 45000; // 45 seconds
  private eventTestTimeout: NodeJS.Timeout | undefined = undefined;
  private retryCnt = 0;
  // #endregion

  private initConnection() {
    const sourceUrl = this.context === 'assortment' ? '/asst/api/bus/events' : `${API_BASE_URL}/events`;
    this.eventSource = new EventSource(`${sourceUrl}?token=${encodeURIComponent(this.accessToken)}`);
    this.expectedEventTypes = new Set<string>(this.eventTypes);

    // setup event listeners
    this.eventSource.addEventListener('open', this.onOpen, false);
    this.eventSource.addEventListener('error', this.onError, false);
    this.expectedEventTypes.forEach((msgType) => {
      this.eventSource?.addEventListener(msgType, this.onMessage, false);
    });

    // start a timer to handle no connection after 5 seconds
    this.noEventsConnectionTimeout = setTimeout(() => this.onNoEventsConnection(), 5000);
  }

  constructor(context: EventSourceContext, observer: Observer<Event>, accessToken: string, eventTypes: string[]) {
    this.context = context;
    this.observer = observer;
    this.accessToken = accessToken;
    this.eventTypes = eventTypes;
    this.initConnection();
  }

  private onOpen = () => {
    // clear retry counter on successful open connection

    this.eventOpened = true;
    // start a timer to log missing events after 10 seconds
    this.missingEventTypesTimeout = setTimeout(() => this.onMissingEventTypes(), 10000);
    // check every second to see if we surpassed our event gap limit. If we do, trigger error to
    // allow for reconnect retry.
    this.eventTestTimeout = setInterval(() => {
      const now = new Date();
      const maxEventGapMs = 1000 * this.MAX_EVENT_GAP_SECONDS;
      if (now.getTime() > this.latestEventTime.getTime() + maxEventGapMs) {
        this.onError(new Event(`No events received in ${this.MAX_EVENT_GAP_SECONDS} seconds.`));
      }
    }, 1000);
  };

  private onMessage = (e: Event) => {
    // store message type for validation that all messages are being received
    const msgType = e.type;
    this.retryCnt = 0;
    this.latestEventTime = new Date();
    this.receivedEventTypes.add(msgType);
    this.observer.next(e);
  };

  private onError = (_err?: Event) => {
    this.cleanup();

    // Retry on error
    // Put some random space in the retry to prevent every connected client hitting at same time
    const retryFuzz = Math.floor(Math.random() * 10) * 100;
    this.retryCnt += 1;
    const exponentialRetryGap = Math.pow(2, this.retryCnt);
    const fullRetryGap = 100 * exponentialRetryGap + retryFuzz;
    // default retry gap to MAX_RETRY_GAP_MS if fullRetryGap exceeds MAX_RETRY_GAP_MS value
    const maxGap = Math.min(fullRetryGap, this.MAX_RETRY_GAP_MS);

    setTimeout(() => {
      this.initConnection();
    }, maxGap);
  };

  private onNoEventsConnection = () => {
    if (!this.eventOpened) {
      // trigger failure state here
      // TODO: want to construct useful event here for logging
      // const err = new ServerSentEventConnectionError(this.context);
      this.onError();
    }

    clearTimeout(this.noEventsConnectionTimeout);
  };

  private onMissingEventTypes = () => {
    const missingEventTypes = Array.from(this.expectedEventTypes).filter((type) => !this.receivedEventTypes.has(type));
    if (missingEventTypes.length > 0) {
      const eventList = missingEventTypes.join(', ');
      service.loggingService.warn(`Missing event types from ${this.context} event source: ${eventList}`);
    }

    clearTimeout(this.missingEventTypesTimeout);
  };

  private cleanup = () => {
    service.loggingService.warn(`Cleaning up all ${this.context} ServerSentEventSource listeners`);

    // this is precautionary in case an error occurs before these
    // event handlers are completed and can clean up their own timeouts
    clearTimeout(this.noEventsConnectionTimeout);
    clearTimeout(this.missingEventTypesTimeout);
    clearTimeout(this.eventTestTimeout);

    forEach(Array.from(this.receivedEventTypes), (msgType) => {
      this.eventSource?.removeEventListener(msgType, this.onMessage);
    });

    this.eventSource?.removeEventListener('error', this.onError, false);
    this.eventSource?.removeEventListener('open', this.onOpen, false);
    this.eventSource?.close();
  };

  public unsubscribe = () => {
    this.cleanup();
  };
}
